/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import React from 'react';
import type { layerTypes, serviceTypes } from '@opengeoweb/store';
import {
  invalidateWMSGetCapabilities,
  queryWMSLayers,
} from '@opengeoweb/webmap';
import type { LayerProps, OGCTYPE } from '@opengeoweb/webmap';
import WMSLayerTreeHeader from './WMSLayerTreeHeader';
import { useCoreTranslation } from '../../../../utils/i18n';

interface WMSLayerTreeProps {
  service: serviceTypes.InitialService;
  onClickLayer: (
    serviceURL: string,
    layerName: string,
    ogctype?: OGCTYPE,
  ) => void;
  highlightedLayers: layerTypes.Layer[];
}

const WMSLayerTree: React.FC<WMSLayerTreeProps> = ({
  service,
  onClickLayer,
  highlightedLayers,
}: WMSLayerTreeProps) => {
  const { t } = useCoreTranslation();

  const [processedOK, setProcessedOK] = React.useState<string[]>([]);
  const handleInternalSelect = (layerName: string, ogctype?: OGCTYPE): void => {
    onClickLayer(service.url, layerName, ogctype);
    setProcessedOK([...processedOK, layerName]);
  };

  const [layers, setLayers] = React.useState<LayerProps[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [forceReload, setForceReload] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const makeServiceQuery = async (): Promise<void> => {
      if (forceReload) {
        await invalidateWMSGetCapabilities(service.url);
      }

      queryWMSLayers(service.url)
        .then((layersResult) => {
          if (isMounted) {
            setLayers(layersResult);
            setIsLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setLayers([]);
            setIsLoading(false);
          }
        });
    };
    void makeServiceQuery();

    return (): void => {
      isMounted = false;
      setForceReload(false);
    };
  }, [service, forceReload]);

  const paperStyle = { height: 200, overflow: 'auto' };

  if (!layers?.length && !isLoading) {
    return (
      <>
        <WMSLayerTreeHeader serviceName={service.name} />
        <Paper data-testid="message" style={paperStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
            }}
          >
            <Typography variant="body2">{t('wms-loader-no-data')}</Typography>
          </Box>
        </Paper>
      </>
    );
  }

  const layerMap = _.groupBy(layers, (layer) =>
    layer.path.length > 1 ? layer.path[layer.path.length - 1] : '',
  );

  // Check if a layer of the same name and service is in an array
  const layerExists = (
    targetLayer: LayerProps,
    layers: layerTypes.Layer[],
  ): boolean => {
    return (
      layers.filter(
        (layerInMap) =>
          layerInMap.name === targetLayer.name &&
          layerInMap.service === service.url,
      ).length > 0
    );
  };

  const categories = Object.keys(layerMap);

  return (
    <>
      <WMSLayerTreeHeader
        onClickReload={(): void => setForceReload(true)}
        serviceName={service.name}
      />
      <Paper style={paperStyle}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <List
            data-testid="layer-list"
            dense
            style={{ backgroundColor: 'inherit' }}
          >
            {categories.map((category) => {
              return (
                <React.Fragment key={category}>
                  {category === '' ? (
                    '' // Do not render a subheader if category is empty
                  ) : (
                    <ListSubheader key={category}>
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          width: '100%',
                          fontWeight: 'bold',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {category}
                      </div>
                    </ListSubheader>
                  )}
                  {layerMap[category].map((layer: LayerProps) => {
                    return layer.leaf ? (
                      <ListItemButton
                        data-testid="selectableLayer"
                        key={layer.name}
                        onClick={(): void =>
                          handleInternalSelect(layer.name!, layer.ogctype)
                        }
                        selected={
                          processedOK.includes(layer.name!) ||
                          layerExists(layer, highlightedLayers)
                        }
                      >
                        <ListItemText id={layer.name!} primary={layer.title} />
                      </ListItemButton>
                    ) : null; // Do not list subcategories as layers
                  })}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </>
  );
};

export default WMSLayerTree;
